import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  ArrowLeft, ArrowRight, ClipboardList, ShieldAlert, Stethoscope,
  Pill, Target, CheckCircle2, AlertTriangle, Ban, History
} from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import FormInput from '../../components/FormInput';
import LoadingSpinner from '../../components/LoadingSpinner';
import { useAuth } from '../../hooks/useAuth';
import * as quizApi from '../../api/quiz.api';
import { errorMessage } from '../../api/client';

// Spec section 4.1 — 4 input steps + a result step. Server is the source of
// truth for BMI, eligibility, and the bottle recommendation; this wizard just
// collects raw answers.
const STEPS = [
  { key: 'basic',    label: 'Basic info',     icon: ClipboardList },
  { key: 'medical',  label: 'Medical history', icon: ShieldAlert },
  { key: 'meds',     label: 'Medications',    icon: Pill },
  { key: 'goals',    label: 'Goals',          icon: Target },
  { key: 'result',   label: 'Result',         icon: CheckCircle2 },
];

const GOAL_OPTIONS = [
  { value: 'WEIGHT_LOSS',         label: 'Weight loss' },
  { value: 'BLOOD_SUGAR_CONTROL', label: 'Blood-sugar control' },
  { value: 'BODY_SHAPING',        label: 'Body shaping' },
];

const CHALLENGE_OPTIONS = [
  { value: 'CANNOT_LOSE_WEIGHT', label: "I can't lose weight on my own" },
  { value: 'POSTPARTUM_WEIGHT',  label: 'Postpartum weight' },
  { value: 'EMOTIONAL_EATING',   label: 'Emotional eating' },
  { value: 'PLATEAU',            label: 'Hit a plateau' },
  { value: 'OTHER',              label: 'Other' },
];

function bmiOf(heightCm, weightKg) {
  const h = Number(heightCm), w = Number(weightKg);
  if (!h || !w) return null;
  const m = h / 100;
  return Math.round((w / (m * m)) * 10) / 10;
}

// Sane physical bounds for the basic-info step. The server enforces the
// same caps (validators/quiz.validators.js) so out-of-range inputs are blocked
// in two places. Most "300+ cm" submissions are typos (e.g. an extra zero),
// so we surface the issue inline before the user clicks Next.
const FIELD_LIMITS = {
  age:      { min: 13,  max: 100, unit: 'years' },
  heightCm: { min: 100, max: 250, unit: 'cm', hint: 'Average adult: ~150–200 cm. Did you mean to type something else?' },
  weightKg: { min: 30,  max: 300, unit: 'kg' },
};

function rangeError(field, value) {
  if (value === '' || value == null) return '';
  const n = Number(value);
  if (Number.isNaN(n)) return 'Please enter a number.';
  const lim = FIELD_LIMITS[field];
  if (!lim) return '';
  if (n < lim.min) return `Must be at least ${lim.min} ${lim.unit}.`;
  if (n > lim.max) return `Must be at most ${lim.max} ${lim.unit}. ${lim.hint || ''}`.trim();
  return '';
}

function StepDots({ stepIndex }) {
  return (
    <div className="row" style={{ gap: 12, marginBottom: 'var(--s-4)', flexWrap: 'wrap' }}>
      {STEPS.map((s, i) => {
        const Icon = s.icon;
        const state = i < stepIndex ? 'done' : i === stepIndex ? 'active' : 'idle';
        return (
          <div key={s.key} className="row" style={{ gap: 8 }}>
            <span
              className="row"
              style={{
                width: 28, height: 28, borderRadius: 999, justifyContent: 'center',
                background: state === 'done' ? 'var(--brand-700)' : state === 'active' ? 'var(--brand-50)' : 'var(--ink-100)',
                color: state === 'done' ? '#fff' : state === 'active' ? 'var(--brand-700)' : 'var(--ink-500)',
                border: state === 'active' ? '2px solid var(--brand-700)' : '0',
              }}
            >
              <Icon size={14} />
            </span>
            <span className="text-sm" style={{ color: state === 'idle' ? 'var(--ink-400)' : 'var(--ink-900)' }}>
              {s.label}
            </span>
            {i < STEPS.length - 1 && <span className="muted">·</span>}
          </div>
        );
      })}
    </div>
  );
}

function YesNoRow({ label, sublabel, value, onChange }) {
  return (
    <div
      className="card"
      style={{
        padding: 'var(--s-3) var(--s-4)',
        marginBottom: 'var(--s-2)',
        background: value ? 'var(--brand-50)' : '#fff',
        borderColor: value ? 'var(--brand-200)' : undefined,
      }}
    >
      <div className="row between" style={{ alignItems: 'flex-start', gap: 16 }}>
        <div style={{ flex: 1 }}>
          <div className="bold">{label}</div>
          {sublabel && <div className="muted text-sm" style={{ marginTop: 4 }}>{sublabel}</div>}
        </div>
        <div className="row" style={{ gap: 6 }}>
          <button
            type="button"
            className={value === false ? 'btn btn-primary' : 'btn btn-outline'}
            onClick={() => onChange(false)}
          >No</button>
          <button
            type="button"
            className={value === true ? 'btn btn-primary' : 'btn btn-outline'}
            onClick={() => onChange(true)}
          >Yes</button>
        </div>
      </div>
    </div>
  );
}

// Only allow same-origin paths so a malicious `?next=https://...` can't redirect off-site.
function safeNext(raw) {
  if (!raw || typeof raw !== 'string') return '';
  if (!raw.startsWith('/') || raw.startsWith('//')) return '';
  return raw;
}

function Quiz() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const nextPath = safeNext(searchParams.get('next'));

  const [stepIndex, setStepIndex] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [loadingExisting, setLoadingExisting] = useState(true);
  const [result, setResult] = useState(null);          // set on fresh submit
  const [previousQuiz, setPreviousQuiz] = useState(null); // shown as a banner only

  // Form state — flat & readable; we shape it before POST.
  const [form, setForm] = useState({
    basic: { name: user?.name || '', age: '', heightCm: '', weightKg: '', contactPhone: user?.phone || '' },
    contraindications: { mtcHistory: null, menType2: null, glp1Allergy: null, pregnantOrTrying: null },
    highRiskFlags: { pancreatitisHistory: null, gallstones: null, severeGastric: null, kidneyProblems: null, chronicVomiting: null },
    medications: { diabetesMedication: null, oralGlucoseMedication: null, weightLossSupplements: null },
    lifestyle: { goal: 'WEIGHT_LOSS', challenges: [], targetLossKg: '', eatingDisorderHistory: false },
  });

  // Surface any prior quiz as a banner at the top, but always start fresh on
  // step 0 so the user is never staring at a "you're eligible" screen they
  // didn't answer for in this session.
  useEffect(() => {
    let cancelled = false;
    quizApi.myLatestQuiz()
      .then((q) => { if (!cancelled && q) setPreviousQuiz(q); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoadingExisting(false); });
    return () => { cancelled = true; };
  }, []);

  function viewPreviousResult() {
    setResult(previousQuiz);
    setStepIndex(STEPS.length - 1);
  }

  const bmi = useMemo(() => bmiOf(form.basic.heightCm, form.basic.weightKg), [form.basic.heightCm, form.basic.weightKg]);

  // Step gating: each step has its own "can advance" rule.
  const basicErrors = useMemo(() => ({
    age:      rangeError('age',      form.basic.age),
    heightCm: rangeError('heightCm', form.basic.heightCm),
    weightKg: rangeError('weightKg', form.basic.weightKg),
  }), [form.basic.age, form.basic.heightCm, form.basic.weightKg]);

  const canAdvance = useMemo(() => {
    if (stepIndex === 0) {
      const b = form.basic;
      const filled = !!(b.name && b.age && b.heightCm && b.weightKg);
      const clean = !basicErrors.age && !basicErrors.heightCm && !basicErrors.weightKg;
      return filled && clean;
    }
    if (stepIndex === 1) {
      // Every contraindication AND high-risk flag must be answered yes/no.
      const allAnswered = (obj) => Object.values(obj).every((v) => v === true || v === false);
      return allAnswered(form.contraindications) && allAnswered(form.highRiskFlags);
    }
    if (stepIndex === 2) {
      const allAnswered = (obj) => Object.values(obj).every((v) => v === true || v === false);
      return allAnswered(form.medications);
    }
    if (stepIndex === 3) {
      return !!form.lifestyle.goal && !!form.lifestyle.targetLossKg;
    }
    return true;
  }, [stepIndex, form]);

  function setBasic(patch)        { setForm((f) => ({ ...f, basic: { ...f.basic, ...patch } })); }
  function setContra(patch)       { setForm((f) => ({ ...f, contraindications: { ...f.contraindications, ...patch } })); }
  function setHighRisk(patch)     { setForm((f) => ({ ...f, highRiskFlags: { ...f.highRiskFlags, ...patch } })); }
  function setMeds(patch)         { setForm((f) => ({ ...f, medications: { ...f.medications, ...patch } })); }
  function setLifestyle(patch)    { setForm((f) => ({ ...f, lifestyle: { ...f.lifestyle, ...patch } })); }

  function toggleChallenge(value) {
    setForm((f) => {
      const has = f.lifestyle.challenges.includes(value);
      return {
        ...f,
        lifestyle: {
          ...f.lifestyle,
          challenges: has ? f.lifestyle.challenges.filter((v) => v !== value) : [...f.lifestyle.challenges, value],
        },
      };
    });
  }

  async function handleSubmit() {
    if (submitting) return;
    setSubmitting(true);
    try {
      const payload = {
        basicInfo: {
          name: form.basic.name?.trim(),
          age: Number(form.basic.age) || undefined,
          heightCm: Number(form.basic.heightCm) || undefined,
          weightKg: Number(form.basic.weightKg) || undefined,
          contactPhone: form.basic.contactPhone?.trim() || undefined,
        },
        contraindications: form.contraindications,
        highRiskFlags: form.highRiskFlags,
        currentMedications: {
          diabetesMedication: !!form.medications.diabetesMedication,
          oralGlucoseMedication: !!form.medications.oralGlucoseMedication,
          weightLossSupplements: !!form.medications.weightLossSupplements,
        },
        lifestyle: {
          goal: form.lifestyle.goal,
          challenges: form.lifestyle.challenges,
          targetLossKg: Number(form.lifestyle.targetLossKg) || undefined,
          eatingDisorderHistory: !!form.lifestyle.eatingDisorderHistory,
        },
      };
      const submitted = await quizApi.submitQuiz(payload);
      setResult(submitted);
      setStepIndex(STEPS.length - 1);
      toast.success('Quiz submitted');
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setSubmitting(false);
    }
  }

  function startOver() {
    setResult(null);
    setStepIndex(0);
  }

  if (loadingExisting) return <LoadingSpinner label="Loading your quiz…" />;

  return (
    <>
      <PageHeader
        title="Eligibility quiz"
        subtitle="2 minutes. We use your answers to confirm safety and recommend a treatment plan."
      />

      {previousQuiz && stepIndex < STEPS.length - 1 && (
        <div
          className="card"
          style={{
            marginBottom: 'var(--s-3)',
            background: 'var(--ink-50)',
            borderColor: 'var(--ink-200)',
          }}
        >
          <div className="row between" style={{ alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <div className="row" style={{ gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <History size={16} />
              <span className="muted text-sm">Last quiz</span>
              <span className={
                previousQuiz.eligibilityStatus === 'ELIGIBLE'    ? 'badge success' :
                previousQuiz.eligibilityStatus === 'CONDITIONAL' ? 'badge warning' :
                'badge danger'
              }>
                {previousQuiz.eligibilityStatus}
              </span>
              <span className="muted text-sm">
                · {new Date(previousQuiz.completedAt || previousQuiz.createdAt).toLocaleDateString()}
              </span>
            </div>
            <button type="button" className="btn btn-link" onClick={viewPreviousResult}>
              View previous result
            </button>
          </div>
        </div>
      )}

      <div className="card">
        <StepDots stepIndex={stepIndex} />

        {/* Step 0 — Basic info */}
        {stepIndex === 0 && (
          <>
            <h3 style={{ marginBottom: 'var(--s-3)' }}>Tell us about yourself</h3>
            <div className="grid grid-2" style={{ gap: 'var(--s-3)' }}>
              <FormInput label="Full name" value={form.basic.name} onChange={(e) => setBasic({ name: e.target.value })} />
              <FormInput
                label="Age"
                type="number"
                inputMode="numeric"
                min={FIELD_LIMITS.age.min}
                max={FIELD_LIMITS.age.max}
                value={form.basic.age}
                onChange={(e) => setBasic({ age: e.target.value })}
                error={basicErrors.age}
              />
              <FormInput
                label="Height (cm)"
                type="number"
                inputMode="numeric"
                min={FIELD_LIMITS.heightCm.min}
                max={FIELD_LIMITS.heightCm.max}
                value={form.basic.heightCm}
                onChange={(e) => setBasic({ heightCm: e.target.value })}
                error={basicErrors.heightCm}
                hint={basicErrors.heightCm ? undefined : 'Enter in centimetres (1 m = 100 cm).'}
              />
              <FormInput
                label="Weight (kg)"
                type="number"
                inputMode="decimal"
                step="0.1"
                min={FIELD_LIMITS.weightKg.min}
                max={FIELD_LIMITS.weightKg.max}
                value={form.basic.weightKg}
                onChange={(e) => setBasic({ weightKg: e.target.value })}
                error={basicErrors.weightKg}
              />
              <FormInput label="Contact phone" value={form.basic.contactPhone} onChange={(e) => setBasic({ contactPhone: e.target.value })} />
              <FormInput label="BMI (auto-calculated)" value={bmi ?? ''} disabled hint="Filled automatically from height + weight." />
            </div>
          </>
        )}

        {/* Step 1 — Medical history (contraindications + high-risk) */}
        {stepIndex === 1 && (
          <>
            <h3 style={{ marginBottom: 'var(--s-2)' }}>Medical history</h3>
            <div className="muted text-sm" style={{ marginBottom: 'var(--s-4)' }}>
              Honesty here is critical — GLP-1 medication is unsafe for some conditions. A "yes" to any
              of the first four questions means we won't be able to enroll you, and we'll point you to
              a doctor instead.
            </div>

            <div className="bold" style={{ marginBottom: 'var(--s-2)', display: 'flex', alignItems: 'center', gap: 8 }}>
              <ShieldAlert size={16} /> Hard contraindications
            </div>
            <YesNoRow label="Personal or family history of Medullary Thyroid Carcinoma (MTC)?" value={form.contraindications.mtcHistory} onChange={(v) => setContra({ mtcHistory: v })} />
            <YesNoRow label="Diagnosed with Multiple Endocrine Neoplasia Type 2 (MEN-2)?" value={form.contraindications.menType2} onChange={(v) => setContra({ menType2: v })} />
            <YesNoRow label="Severe allergy to GLP-1 medications (e.g. semaglutide, tirzepatide)?" value={form.contraindications.glp1Allergy} onChange={(v) => setContra({ glp1Allergy: v })} />
            <YesNoRow label="Pregnant, planning pregnancy, or breastfeeding?" value={form.contraindications.pregnantOrTrying} onChange={(v) => setContra({ pregnantOrTrying: v })} />

            <div className="bold" style={{ marginTop: 'var(--s-4)', marginBottom: 'var(--s-2)', display: 'flex', alignItems: 'center', gap: 8 }}>
              <Stethoscope size={16} /> Doctor review (high-risk flags)
            </div>
            <div className="muted text-sm" style={{ marginBottom: 'var(--s-3)' }}>
              "Yes" to any of these doesn't block you, but the clinic doctor will need to clear you before
              your first injection.
            </div>
            <YesNoRow label="History of pancreatitis?" value={form.highRiskFlags.pancreatitisHistory} onChange={(v) => setHighRisk({ pancreatitisHistory: v })} />
            <YesNoRow label="Gallstones or gallbladder inflammation?" value={form.highRiskFlags.gallstones} onChange={(v) => setHighRisk({ gallstones: v })} />
            <YesNoRow label="Severe gastric issues or gastroparesis?" value={form.highRiskFlags.severeGastric} onChange={(v) => setHighRisk({ severeGastric: v })} />
            <YesNoRow label="Known kidney problems?" value={form.highRiskFlags.kidneyProblems} onChange={(v) => setHighRisk({ kidneyProblems: v })} />
            <YesNoRow label="Chronic vomiting or other ongoing digestive issues?" value={form.highRiskFlags.chronicVomiting} onChange={(v) => setHighRisk({ chronicVomiting: v })} />
          </>
        )}

        {/* Step 2 — Current medications */}
        {stepIndex === 2 && (
          <>
            <h3 style={{ marginBottom: 'var(--s-3)' }}>Current medications</h3>
            <div className="muted text-sm" style={{ marginBottom: 'var(--s-3)' }}>
              We share these with the clinic doctor so they can adjust the prescription safely.
            </div>
            <YesNoRow label="On any diabetes medication (insulin, etc.)?" value={form.medications.diabetesMedication} onChange={(v) => setMeds({ diabetesMedication: v })} />
            <YesNoRow label="On any oral glucose-lowering medication?" value={form.medications.oralGlucoseMedication} onChange={(v) => setMeds({ oralGlucoseMedication: v })} />
            <YesNoRow label="Currently using weight-loss supplements?" value={form.medications.weightLossSupplements} onChange={(v) => setMeds({ weightLossSupplements: v })} />
          </>
        )}

        {/* Step 3 — Goals */}
        {stepIndex === 3 && (
          <>
            <h3 style={{ marginBottom: 'var(--s-3)' }}>Your goals</h3>

            <div className="field">
              <label>Primary goal</label>
              <div className="row wrap" style={{ gap: 8 }}>
                {GOAL_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    className={form.lifestyle.goal === opt.value ? 'btn btn-primary' : 'btn btn-outline'}
                    onClick={() => setLifestyle({ goal: opt.value })}
                  >{opt.label}</button>
                ))}
              </div>
            </div>

            <div className="field" style={{ marginTop: 'var(--s-3)' }}>
              <label>What's holding you back? (Pick any that apply)</label>
              <div className="row wrap" style={{ gap: 8 }}>
                {CHALLENGE_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    className={form.lifestyle.challenges.includes(opt.value) ? 'btn btn-primary' : 'btn btn-outline'}
                    onClick={() => toggleChallenge(opt.value)}
                  >{opt.label}</button>
                ))}
              </div>
            </div>

            <div className="grid grid-2" style={{ gap: 'var(--s-3)', marginTop: 'var(--s-3)' }}>
              <FormInput
                label="Target weight loss (kg)"
                type="number"
                value={form.lifestyle.targetLossKg}
                onChange={(e) => setLifestyle({ targetLossKg: e.target.value })}
                hint="We use this to recommend a 1-, 2-, or 3-bottle plan."
              />
            </div>

            <label className="row" style={{ gap: 8, marginTop: 'var(--s-3)' }}>
              <input
                type="checkbox"
                checked={!!form.lifestyle.eatingDisorderHistory}
                onChange={(e) => setLifestyle({ eatingDisorderHistory: e.target.checked })}
              />
              <span>I have a current or past history of an eating disorder.</span>
            </label>
          </>
        )}

        {/* Step 4 — Result */}
        {stepIndex === STEPS.length - 1 && result && (
          <ResultCard quiz={result} onRestart={startOver} navigate={navigate} nextPath={nextPath} />
        )}

        {/* Footer nav (hidden on result step) */}
        {stepIndex < STEPS.length - 1 && (
          <div className="row between" style={{ marginTop: 'var(--s-5)' }}>
            <button
              type="button"
              className="btn btn-outline"
              onClick={() => setStepIndex((i) => Math.max(0, i - 1))}
              disabled={stepIndex === 0}
            >
              <ArrowLeft size={14} /> Back
            </button>

            {stepIndex < STEPS.length - 2 ? (
              <button
                type="button"
                className="btn btn-primary"
                disabled={!canAdvance}
                onClick={() => setStepIndex((i) => i + 1)}
              >
                Next <ArrowRight size={14} />
              </button>
            ) : (
              <button
                type="button"
                className="btn btn-primary"
                disabled={!canAdvance || submitting}
                onClick={handleSubmit}
              >
                {submitting ? 'Submitting…' : 'See my result'} <ArrowRight size={14} />
              </button>
            )}
          </div>
        )}
      </div>
    </>
  );
}

function ResultCard({ quiz, onRestart, navigate, nextPath }) {
  const status = quiz.eligibilityStatus;
  const pkg = quiz.recommendedPackage;
  // Prefer an explicit ?next= target (the plan the user came from). Else
  // fall back to the recommended/matched plan; else the catalogue.
  const continueTo = nextPath || (pkg?._id ? `/packages/${pkg._id}` : '/packages');
  const continueLabel = nextPath
    ? 'Continue with selected plan'
    : pkg?._id ? 'View this plan' : 'Browse plans';

  if (status === 'NOT_ELIGIBLE') {
    return (
      <div>
        <div className="row" style={{ gap: 12, alignItems: 'flex-start' }}>
          <span style={{
            width: 48, height: 48, borderRadius: 999, background: 'var(--rose-50, #fef2f2)',
            color: 'var(--rose-600, #dc2626)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center'
          }}>
            <Ban size={24} />
          </span>
          <div style={{ flex: 1 }}>
            <h2 style={{ marginBottom: 4 }}>Please consult a doctor first</h2>
            <div className="muted">{quiz.eligibilityReason}</div>
          </div>
        </div>

        <div className="card" style={{ marginTop: 'var(--s-4)', background: 'var(--ink-50)' }}>
          <div className="bold" style={{ marginBottom: 8 }}>What happens next</div>
          <div className="muted">
            Based on your answers, GLP-1 weight-loss treatment isn't a safe fit right now. We won't take
            a booking — instead, please discuss your goals with your own doctor first. If your situation
            changes, you're welcome to retake the quiz.
          </div>
        </div>

        <div className="row" style={{ gap: 8, marginTop: 'var(--s-4)' }}>
          <button type="button" className="btn btn-outline" onClick={onRestart}>Retake quiz</button>
          <Link to="/dashboard" className="btn btn-primary">Back to dashboard</Link>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="row" style={{ gap: 12, alignItems: 'flex-start' }}>
        <span style={{
          width: 48, height: 48, borderRadius: 999,
          background: status === 'CONDITIONAL' ? 'var(--amber-50, #fffbeb)' : 'var(--emerald-50, #ecfdf5)',
          color: status === 'CONDITIONAL' ? 'var(--amber-700, #b45309)' : 'var(--emerald-700, #047857)',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center'
        }}>
          {status === 'CONDITIONAL' ? <AlertTriangle size={24} /> : <CheckCircle2 size={24} />}
        </span>
        <div style={{ flex: 1 }}>
          <h2 style={{ marginBottom: 4 }}>
            {status === 'CONDITIONAL' ? 'You may be eligible — doctor approval required' : 'Great news — you look eligible'}
          </h2>
          <div className="muted">
            {status === 'CONDITIONAL'
              ? (quiz.eligibilityReason || 'A clinic doctor will confirm before your first injection.')
              : 'Choose your treatment plan below — a clinic will be assigned automatically after payment, and you can schedule your first session from your booking.'}
          </div>
        </div>
      </div>

      {quiz.recommendedBottles ? (
        <div className="card" style={{ marginTop: 'var(--s-4)', background: 'var(--brand-50)', borderColor: 'var(--brand-200)' }}>
          <div className="row between" style={{ alignItems: 'flex-start', flexWrap: 'wrap', gap: 16 }}>
            <div>
              <div className="text-xs" style={{ textTransform: 'uppercase', letterSpacing: '0.18em', color: 'var(--brand-700)', fontWeight: 600 }}>
                Recommended for you
              </div>
              <div style={{ fontSize: 22, fontWeight: 600, marginTop: 4 }}>
                {quiz.recommendedBottles}-bottle plan · {quiz.recommendedWeeks} weeks
              </div>
              {pkg && (
                <div className="muted" style={{ marginTop: 4 }}>
                  Matched plan: <span className="bold">{pkg.name}</span> · from {pkg.currency || 'MYR'} {pkg.price?.toLocaleString?.()}
                </div>
              )}
            </div>
            <div className="row" style={{ gap: 8 }}>
              <button type="button" className="btn btn-outline" onClick={onRestart}>Retake</button>
              <Link to={continueTo} className="btn btn-primary">
                {continueLabel} <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </div>
      ) : null}

      {quiz.suggestedLabs?.length > 0 && (
        <div style={{ marginTop: 'var(--s-4)' }}>
          <div className="bold" style={{ marginBottom: 8 }}>Suggested labs (optional add-on)</div>
          <div className="muted text-sm" style={{ marginBottom: 8 }}>
            These tests give your clinic doctor a clearer picture before treatment.
          </div>
          <div className="row wrap" style={{ gap: 8 }}>
            {quiz.suggestedLabs.map((l) => <span key={l} className="badge brand">{l}</span>)}
          </div>
        </div>
      )}
    </div>
  );
}

export default Quiz;
